//—— 問卦的過場演出：一支影片 + 幾句台詞，講完讓路 ——
//
//  目前有兩幕走這支（資料表在 fuyaozi-ask-page 的 SCENES）：
//    welcome  進門時伏爻子看出你有事要問
//    restate  你送出問題之後，他說要幫你卜卦
//  動畫 3（卜卦中）接上來時也是同一個形狀，表裡多一筆就好。
//
//  —— 為什麼合成一支 ——
//  這兩幕原本是各自一支元件（intro/ 與 restate/），只差台詞與影片檔名 ——
//  202 行與 191 行，差異只有 85 行而且幾乎都是名稱。
//  第三幕再複製一份就是三處要同步修的時序邏輯（StrictMode 的存活旗標、
//  自動播放被擋的退路、載不出來要讓路），那種東西分三份一定會分叉。
//
//  —— 什麼不該併進來 ——
//  搖龜殼那一幕不要。它會長出完全不同的東西（可互動的龜殼、六爻一爻一爻
//  揭露、搖到一半離開還能接回來），併進來會讓這支變成一個誰都不好改的共用層。
//  這支只負責「播一支影片、照順序講幾句話、講完讓路」。

import { useEffect, useMemo, useRef, useState } from "react";

import "./fuyaozi-scene-style.scss";
import videoUrl from "../../../../utils/video-url";

//跳過影片開頭那一格。理由同白夜的 INTRO_SKIP_S：
//  第 0 格是一張還沒動起來的靜止畫面，從那裡起播會先愣一下。
const SKIP_S = 0.3;

//一句台詞停多久。短句吃 1500 的下限，長句按字數加 ——
//  數值與白夜一致，兩個角色說話的節奏才像同一個世界的人。
const lineDuration = (text) => Math.max(1500, 900 + String(text).length * 100);

//一句裡最長的一段有幾個字（字級算式要用，見 drama-line mixin）
const longestRun = (text) =>
  String(text || "")
    .split("\n")
    .reduce((max, part) => Math.max(max, part.length), 0);

//台詞講完之後、讓出畫面之前留的一口氣。
//  最後一個字才剛淡掉就切走，看起來像被彈出去的。
const BEAT_MS = 600;

//把資料表裡的台詞正規化成 { text, max }。
//  只有要放大的那種喊話句才需要寫 max（見白夜 SCENES 的「來吧！」），
//  其餘的不寫 —— drama-line 的 var() 退路 42px 會接手。
const normalizeLine = (line) =>
  typeof line === "string" ? { text: line, max: 0 } : { max: 0, ...line };

//—— 什麼時候離場 ——
//
//  台詞講完就走，不等影片播完（兩支影片都有 10 秒，台詞大約 5 秒）。
//
//  因為歡迎那一幕每次進頁面都會播：讓人每次都坐等五秒沒有字的畫面，
//  第三次進來就開始煩了。影片提早結束（載不出來、解碼失敗）也一樣讓路，
//  兩個條件誰先到算誰。
//—— 聲音跨幕記住 ——
//
//  三幕（歡迎／覆述／斷卦）是三次掛載。瀏覽器擋掉有聲自動播放時，
//  不記住的話每一幕都會再被擋一次、再跳一次「開啟聲音」，使用者要按三遍。
//
//  放在模組層而不是 state：它要跨元件的掛載活著，而且改它不該觸發重繪。
//  不落 storage —— 這是「這次瀏覽解鎖過沒」，不是使用者的偏好設定。
let soundUnlocked = false;

const FuyaoziScene = ({ scene, onDone }) => {
  const [lineIndex, setLineIndex] = useState(0);
  //影片真的動起來了才讓字出現 —— 直接顯示的話字會在畫面還黑的時候就跑掉
  const [playing, setPlaying] = useState(false);
  const [leaving, setLeaving] = useState(false);

  //瀏覽器擋掉有聲自動播放時退回靜音，並露出「開啟聲音」。
  //  問卦是從底部頁籤進來的，使用者未必在本頁互動過，所以這條路徑比
  //  白夜那邊更容易走到（他是從劇情頁一路點進去的）。
  const [muted, setMuted] = useState(false);
  const [soundBlocked, setSoundBlocked] = useState(false);

  const videoRef = useRef(null);
  //離場只能發生一次。影片 ended 與台詞講完可能幾乎同時到，
  //  不擋的話 onDone 會被呼叫兩次 —— 外層的 setState 會跑兩輪。
  const leftRef = useRef(false);

  const lines = useMemo(
    () => (scene?.lines || []).map(normalizeLine),
    [scene],
  );
  const leadIn = scene?.leadIn ?? 0;
  const src = videoUrl(scene?.video);

  //⚠️ 一定要在 effect 裡重設。StrictMode 下是「掛載 → cleanup → 再掛載」，
  //  而 useRef 的初始值只在第一次掛載套用 —— 寫成 useRef(false) 然後
  //  只在 cleanup 翻 true 的話，第二次掛載會帶著上一輪的值進來。
  useEffect(() => {
    leftRef.current = false;
    return () => {
      leftRef.current = true;
    };
  }, []);

  const leave = () => {
    if (leftRef.current) return;
    leftRef.current = true;
    //先淡出再通知，不然底下的頁面會在影片還在畫面上時就冒出來
    setLeaving(true);
    setTimeout(() => onDone?.(), BEAT_MS);
  };

  //—— 播放 ——
  //  帶聲音的自動播放被擋就退回靜音續播。畫面不能因為沒聲音就停住。
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    video.currentTime = SKIP_S;

    //  聲音是這幾幕的一部分（伏爻子在說話），所以**預設就開著**，
    //  被擋才退回靜音續播 —— 畫面不能因為沒聲音就停住。
    video.muted = false;

    //  被擋之後有兩條回來的路：
    //    1. 使用者按「開啟聲音」
    //    2. 使用者在畫面上做了任何動作（按禁忌的確認、滑龜殼、點一下）——
    //       那一刻瀏覽器就認得這個手勢了，直接把聲音接上，不用他特地去按。
    //  兩條都會立起 soundUnlocked，後面兩幕就不會再被擋。
    let unlock = null;
    const clearUnlock = () => {
      if (!unlock) return;
      document.removeEventListener("pointerdown", unlock);
      unlock = null;
    };

    video.play().then(
      () => {
        soundUnlocked = true;
      },
      () => {
        setMuted(true);
        //  之前已經解鎖過就不要再跳一次「開啟聲音」——
        //  他早就表達過要聽，再問一次是在煩他。
        //  下一個點擊會由底下那支 unlock 自己把聲音接回來。
        setSoundBlocked(!soundUnlocked);
        video.muted = true;
        video.play().catch(() => {
          //連靜音都播不動（檔案壞了、解碼失敗）：不要卡在這裡，直接讓路
          leave();
        });

        //  監聽 document 而不是這一層 —— 禁忌 dialog、底部頁籤都在外面
        unlock = () => {
          unlock = null;
          const el = videoRef.current;
          if (!el) return;
          el.muted = false;
          el.play().then(
            () => {
              soundUnlocked = true;
              setMuted(false);
              setSoundBlocked(false);
            },
            //  還是不行就維持靜音，「開啟聲音」那顆鈕留著
            () => {
              el.muted = true;
            },
          );
        };
        document.addEventListener("pointerdown", unlock, { once: true });
      },
    );

    return clearUnlock;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //—— 台詞一句一句往下推 ——
  //  影片真的動起來才起算，不然字會在畫面還黑的時候就跑掉。
  useEffect(() => {
    if (!playing) return undefined;

    //—— 沒有台詞的幕：播到影片結束 ——
    //
    //  這種幕是拿來「蓋住後台在跑的事」的（動畫 3 掩護 AI 斷卦），
    //  它的價值就在於那十幾秒的長度 —— 照下面的規則走會在 600ms
    //  之後就離場，等於什麼都沒蓋到。
    //  收尾交給 <video> 的 onEnded（載不出來時則是 onError）。
    if (!lines.length) return undefined;

    if (lineIndex >= lines.length) {
      //講完了，留一口氣就讓路
      const timer = setTimeout(leave, BEAT_MS);
      return () => clearTimeout(timer);
    }
    const wait =
      lineDuration(lines[lineIndex].text) + (lineIndex === 0 ? leadIn : 0);
    const timer = setTimeout(() => setLineIndex((i) => i + 1), wait);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, lineIndex, lines, leadIn]);

  const line = lineIndex < lines.length ? lines[lineIndex] : null;

  return (
    <div
      className={`fuyaozi-scene${leaving ? " is-leaving" : ""}`}
      //純演出，不是可以操作的東西
      aria-hidden="true"
    >
      <video
        ref={videoRef}
        className={`fuyaozi-scene__video${playing ? " is-front" : ""}`}
        src={src}
        muted={muted}
        playsInline
        preload="auto"
        //不能用 loop：要靠 ended 收尾，而循環的影片永遠不會發。
        onLoadedMetadata={(e) => {
          e.currentTarget.currentTime = SKIP_S;
        }}
        onPlaying={() => setPlaying(true)}
        onEnded={leave}
        //載不出來就讓路，不要把人卡在一片黑裡
        onError={leave}
      />

      {/* key 綁 lineIndex：換句時整個重新掛載，淡入淡出才會重播 */}
      {line && playing && (
        <div className="fuyaozi-scene__center">
          <p
            className="fuyaozi-scene__line"
            key={lineIndex}
            style={{
              "--line-chars": longestRun(line.text),
              ...(line.max ? { "--line-max": `${line.max}px` } : null),
              animationDuration: `${lineDuration(line.text)}ms`,
              ...(lineIndex === 0 && leadIn
                ? { animationDelay: `${leadIn}ms` }
                : null),
            }}
          >
            {line.text}
          </p>
        </div>
      )}

      {soundBlocked && (
        <button
          type="button"
          className="fuyaozi-scene__sound"
          onClick={() => {
            //  這是明確的使用者手勢，一定放得出聲音。
            //  立旗標讓後面兩幕不用再問一次。
            soundUnlocked = true;
            const el = videoRef.current;
            if (el) {
              el.muted = false;
              el.play().catch(() => {});
            }
            setMuted(false);
            setSoundBlocked(false);
          }}
        >
          <span aria-hidden="true">🔇</span>
          開啟聲音
        </button>
      )}
    </div>
  );
};

export default FuyaoziScene;
